import { z } from "zod";

import {
  RecurringEndType,
  RecurringFrequency,
  WeekDay,
} from "@classview/shared-types";

export enum SessionVisibility {
  PRIVATE = "PRIVATE",
  ORGANISATION_WIDE = "ORGANISATION_WIDE",
}

const guestSchema = z.object({
  userId: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  isExternal: z.boolean().optional(),
  isModerator: z.boolean().optional(),
  isHost: z.boolean().optional(),
});

const addSessionSchemaBase = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  description: z.string().optional(),
  startDate: z.date(),
  startTime: z.date(),
  endTime: z.date(),
  visibility: z.nativeEnum(SessionVisibility, {
    errorMap: () => ({ message: "Visibility is required" }),
  }),
  isScheduledToRecord: z.boolean().optional(),
  isParticipantScreenSharing: z.boolean().optional(),
  isChatEnabled: z.boolean().optional(),
  isRaisedHandSoundEnabled: z.boolean().optional(),
  isClosedCaptionsEnabled: z.boolean().optional(),
  isRecurring: z.boolean().optional(),
  isConfidential: z.boolean().default(false),
  recurringFrequency: z.nativeEnum(RecurringFrequency).optional(),
  isRepeatOnWeekdaysOnly: z.boolean().optional(),
  weeklyDays: z.array(z.nativeEnum(WeekDay)).optional(),
  weeklyInterval: z
    .union([z.string().transform((val) => parseInt(val, 10)), z.number()])
    .optional(),
  nbRepetitions: z
    .union([z.string().transform((val) => parseInt(val, 10)), z.number()])
    .optional(),
  endDate: z.date().optional(),
  endType: z.nativeEnum(RecurringEndType).optional(),
  guests: z
    .array(guestSchema)
    .min(1, { message: "At least one guest is required" }),
  roomSystems: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      organisationId: z.string(),
      organisationName: z.string(),
      conflictingSessions: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          start: z.string(),
          end: z.string(),
        }),
      ),
    }),
  ),
  invitedOrganisations: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      status: z.string(),
    }),
  ),
});

const addSessionSchema = addSessionSchemaBase
  .refine(
    (data) => {
      return data.startTime < data.endTime;
    },
    {
      message: "Invalid time range",
      path: ["endTime"],
    },
  )
  .refine(
    (data) => {
      if (data.isRecurring && data.endType === RecurringEndType.DATE) {
        return data.endDate !== undefined;
      }
      return true;
    },
    {
      message: "End date is required for recurring sessions with DATE end type",
      path: ["endDate"],
    },
  )
  .refine(
    (data) => {
      if (
        data.isRecurring &&
        data.endType === RecurringEndType.NB_REPETITIONS
      ) {
        return data.nbRepetitions !== undefined;
      }
      return true;
    },
    {
      message:
        "Number of repetitions is required for recurring sessions with NB_REPETITIONS end type",
      path: ["nbRepetitions"],
    },
  );

export const acceptSessionSchema = z.object({
  guests: z.array(guestSchema),
  roomSystems: addSessionSchemaBase.shape.roomSystems,
});

type AddSessionFormData = z.infer<typeof addSessionSchema>;

export { addSessionSchema };
export type { AddSessionFormData };
